import { inject } from '@angular/core';
import { RedirectCommand, ResolveFn, Router } from '@angular/router';
import { catchError, of, take, throwError } from 'rxjs';

import { ClassificationRatesService } from '../services/classification-rates.service';
import { LCPUserService } from '@shared/services/lcp-user/lcp-user.service';
import { ClassificationRateByIdQueryVars } from '../copy-master-wage-data/interfaces/classification-rates-query-vars.interface';
import { ClassificationRatesResponse } from '../interfaces/classification-rates-response.interface';
import { WageRateType } from '../enums/wage-rate-type.enum';
import { SortEnumType } from 'src/app/services/rbs/rbs.types';
import { MatSnackBar } from '@angular/material/snack-bar';

export const classificationRatesResolver: ResolveFn<
  ClassificationRatesResponse
> = route => {
  // Dependency Injection
  const _classificationRatesService = inject(ClassificationRatesService);
  const _lcpUserService = inject(LCPUserService);
  const _router = inject(Router);
  const _snackbarService = inject(MatSnackBar);

  // Extract variables
  const wageDeterminationModificationId: string | null = route.paramMap.get(
    'wageDeterminationModificationId'
  );
  const jurisdictionId: string | null = route.paramMap.get('jurisdictionId');
  const wageLocationId: string | null = route.paramMap.get('wageLocationId');
  const constructionTypeId: string | null =
    route.paramMap.get('constructionTypeId');

  const sessionTenantId: string = _lcpUserService.getUserTenantIdValue();
  const fallbackUrl = '/setup/prevailing-wage-setup/manage-master-wage-data';
  const redirectCommand = new RedirectCommand(_router.parseUrl(fallbackUrl));

  // Validate incoming variables
  if (!wageDeterminationModificationId) {
    return throwError(
      () =>
        new Error(
          'No Wage Determination Modification Id was provided. Please Contact Support.'
        )
    );
  }

  if (!jurisdictionId) {
    return throwError(
      () =>
        new Error('No Jurisdiction Id was provided. Please Contact Support.')
    );
  }

  // Build query variables
  const vars: ClassificationRateByIdQueryVars = {
    skip: 0,
    take: 10,
    tenantId: sessionTenantId,
    jurisdictionId,
    where: {
      and: [
        {
          wageDeterminationModificationId: {
            eq: wageDeterminationModificationId,
          },
        },
        {
          jurisdictionLocation: {
            wageLocationId: {
              eq: wageLocationId,
            },
          },
        },
        {
          wageRateType: {
            name: {
              eq: WageRateType.MasterWage,
            },
          },
        },
        {
          constructionType: {
            id: {
              eq: constructionTypeId,
            },
          },
        },
      ],
    },
    order: [
      {
        craftClassification: {
          craft: {
            name: SortEnumType.Asc,
          },
        },
      },
      {
        craftClassification: {
          name: SortEnumType.Asc,
        },
      },
      {
        issueDate: SortEnumType.Asc,
      },
    ],
  };

  return _classificationRatesService
    .getClassificationRateById(vars, 'network-only')
    .pipe(
      take(1),
      catchError(() => {
        _snackbarService.open(
          'Something went wrong, try again later',
          'DISMISS',
          { duration: 5000, verticalPosition: 'top' }
        );
        return of(redirectCommand);
      })
    );
};
